import { Geometry, Rect ,MyPIXIContainer } from "./MyHelpers";
import { FitType, FillingPotFeature, JackpotDisplayType, Settings, VirtualBotoneraType } from "./Settings";

export enum Orientation
{
	Landscape = "Landscape",
	Portrait = "Portrait"
};

export type ViewportSize = {
	width: number;
	height: number;
};

export function GetViewportSize(): ViewportSize
{
	const host = document.querySelector<HTMLElement>(".singlegame")
		?? document.querySelector<HTMLElement>(".multigameViewBackground")
		?? document.querySelector<HTMLElement>(".superscreen");
	if (host != null) {
		const hostWidth = host.clientWidth;
		const hostHeight = host.clientHeight;
		if (hostWidth > 1 && hostHeight > 1) {
			return {
				width: Math.max(1, Math.round(hostWidth)),
				height: Math.max(1, Math.round(hostHeight)),
			};
		}
	}

	const viewport = window.visualViewport;
	const width = viewport?.width ?? document.documentElement?.clientWidth ?? window.innerWidth;
	const height = viewport?.height ?? document.documentElement?.clientHeight ?? window.innerHeight;

	return {
		width: Math.max(1, Math.round(width)),
		height: Math.max(1, Math.round(height)),
	};
}


export function GetOrientation(): Orientation
{
	// Prefer the same viewport metrics used by the PIXI canvas. In Chrome
	// device mode, matchMedia(orientation) can follow the outer browser
	// window (often landscape) instead of the emulated phone size.
	const { width, height } = GetViewportSize();
	return width >= height ? Orientation.Landscape : Orientation.Portrait;
}

/** Shared portrait recipe so pots, reels, controls and panel scale from the same viewport. */
export type PortraitSlotLayout = {
	viewW: number;
	viewH: number;
	jackpotPercentY: number;
	jackpotPercentH: number;
	potsPercentY: number;
	potsPercentH: number;
	reelsPercentY: number;
	reelsPercentH: number;
	panelPercentY: number;
	panelPercentH: number;
	/** Pixel height of the bottom HTML panel host (width-only; stable across fullscreen). */
	panelHeightPx: number;
	/** Pixel gap under Spin so it sits above the panel (width-only). */
	controlsBottomPx: number;
	spinSizePx: number;
	sideButtonSizePx: number;
	controlsGapPx: number;
	controlsBottomPercent: number;
	controlsPercentH: number;
	winBandPercentH: number;
	winWidthPx: number;
	winHeightPx: number;
	doubleUpSizePx: number;
	winDoubleUpGapPx: number;
};

export function GetPortraitSlotLayout(): PortraitSlotLayout {
	const viewport = GetViewportSize();
	const viewW = viewport.width;
	const viewH = viewport.height;

	// Bottom HTML panel ignores viewport height (fullscreen). Width-only fit of 1575x300.
	const panelFit = viewW / 1575;
	const panelHeightPx = Math.round(300 * panelFit);
	const controlsBottomPx = panelHeightPx + 8;
	const panelPercentH = panelHeightPx / viewH;
	const panelPercentY = 1 - panelPercentH;

	const widthFitReels = (viewW * 1080 / 1920) / viewH;
	const withPots = Settings.fillingPotFeature === FillingPotFeature.Enable;
	const isShortPortrait = viewW / viewH >= 0.62;
	const jackpotPercentH = isShortPortrait ? 0.20 : 0.18;
	let reelsPercentH = withPots
		? Math.min(isShortPortrait ? 0.36 : 0.44, Math.max(isShortPortrait ? 0.30 : 0.32, widthFitReels))
		: Math.min(isShortPortrait ? 0.42 : 0.48, Math.max(0.28, widthFitReels));
	const potsPercentH = withPots
		? (isShortPortrait ? 0.26 : 0.24)
		: (isShortPortrait ? 0.16 : 0.14);
	const minTop = 0;

	const jackpotPercentY = minTop;
	let potsPercentY = jackpotPercentY + jackpotPercentH;
	let reelsPercentY = potsPercentY + potsPercentH;

	const winHeightPx = Math.max(48, Math.round(Math.min(0.07 * viewH, 68)));
	const winBandPercentH = (winHeightPx + 8) / viewH;
	// En portrait corto, el mínimo de rieles y el mínimo de Spin no pueden
	// reservarse de forma independiente: terminaban ocupando más de la altura
	// disponible y el Spin se superponía a los rieles. Primero se reserva la
	// franja del panel, WIN y los controles; los rieles usan el espacio restante.
	const minSpinPx = Math.max(48, Math.round(Math.min(viewW * 0.13, viewH * 0.08)));
	const minControlsPercentH = (minSpinPx + 8) / viewH;
	const maxReelsPercentH = 1
		- panelPercentH
		- 0.008
		- winBandPercentH
		- minControlsPercentH
		- jackpotPercentH
		- potsPercentH
		- minTop;
	reelsPercentH = Math.min(reelsPercentH, Math.max(0.18, maxReelsPercentH));
	const reelsEnd = reelsPercentY + reelsPercentH;

	const maxControlsPercentH = Math.max(0.065, 1 - panelPercentH - winBandPercentH - reelsEnd - 0.004);
	const maxSpinPx = maxControlsPercentH * viewH - 8;
	let baseSpinPx = Math.round(Math.min(viewW * 0.24, viewH * 0.10, maxSpinPx));
	baseSpinPx = Math.max(56, Math.min(baseSpinPx, Math.round(maxSpinPx)));
	let sideButtonSizePx = Math.round(baseSpinPx * 0.68);
	let controlsGapPx = Math.round(baseSpinPx * 0.12);

	// Keep reels where they were so WIN can sit on the frame. Spin then
	// grows into the cobblestone that used to sit between WIN and the reels.
	const dropReserveBand = isShortPortrait ? 0.05 : 0.04;
	const baseControlsPercentH = (baseSpinPx + 8) / viewH;
	const winTop = 1 - panelPercentH - baseControlsPercentH - dropReserveBand;
	const drop = Math.max(0, winTop - reelsEnd - 0.008);
	const reelsHCap = withPots
		? (isShortPortrait ? 0.36 : 0.44)
		: (isShortPortrait ? 0.42 : 0.50);
	const grow = Math.min(drop, Math.max(0, reelsHCap - reelsPercentH));
	reelsPercentH += grow;
	const shift = drop - grow;
	potsPercentY += shift;
	reelsPercentY += shift;
	const reelsEndAfter = reelsPercentY + reelsPercentH;

	const roomForSpin = Math.max(
		baseSpinPx,
		(1 - panelPercentH - 0.008 - winBandPercentH - reelsEndAfter) * viewH
	);
	let spinSizePx = Math.round(Math.min(baseSpinPx * 1.32, viewW * 0.34, viewH * 0.145, roomForSpin));
	spinSizePx = Math.max(baseSpinPx, spinSizePx);
	const controlsRowMax = viewW * 0.96;
	const sideRowWidth = sideButtonSizePx * 2 + controlsGapPx * 2;
	if (spinSizePx + sideRowWidth > controlsRowMax) {
		spinSizePx = Math.max(baseSpinPx, Math.round(controlsRowMax - sideRowWidth));
	}
	const controlsPercentH = (spinSizePx + 8) / viewH;
	const controlsBottomPercent = panelPercentH + 0.008;

	const winWidthPx = Math.round(Math.min(viewW * 0.36, winHeightPx * 3.2));
	const doubleUpSizePx = Math.round(Math.min(winHeightPx * 0.78, winBandPercentH * viewH * 0.66));
	const winDoubleUpGapPx = Math.round(Math.max(6, Math.min(viewW * 0.035, doubleUpSizePx * 0.42)));

	return {
		viewW,
		viewH,
		jackpotPercentY,
		jackpotPercentH,
		potsPercentY,
		potsPercentH,
		reelsPercentY,
		reelsPercentH,
		panelPercentY,
		panelPercentH,
		panelHeightPx,
		controlsBottomPx,
		spinSizePx,
		sideButtonSizePx,
		controlsGapPx,
		controlsBottomPercent,
		controlsPercentH,
		winBandPercentH,
		winWidthPx,
		winHeightPx,
		doubleUpSizePx,
		winDoubleUpGapPx,
	};
}

/** Classic, Diamond Pot, Rapid Link and Filling Pot: base-game reels use the full play width. */
export function UsesFullWidthBaseGameReels(): boolean {
	const type = String(Settings.JackpotDisplayType ?? "");
	if (/Portal|Mystic/i.test(type)) {
		return false;
	}
	if (Settings.JackpotDisplayType === JackpotDisplayType.PaytableJackpotDisplay) {
		return true;
	}
	if (Settings.fillingPotFeature === FillingPotFeature.Enable) {
		return true;
	}
	return /DiamondPot|Rapid/i.test(type);
}

export function GetFitType()
{
    let url = new URL(window.location.href);
    let params = new URLSearchParams(url.search);

    if (params.has("stretch"))
        return "stretch";
    
    if (params.has("fit"))
        return "fit";

    return Settings.DefaultFitType;
}

export const currentFitType: FitType = GetFitType();


export function FitScreen(fittype: FitType): void
{
	const viewport = GetViewportSize();
	let windowRect: Rect = new Rect(0, 0, viewport.width, viewport.height);
	//let screen = document.querySelector<HTMLElement>(".screen");
	let screen = document.querySelector<HTMLElement>(".singlegame") ?? document.querySelector<HTMLElement>(".superscreen")


	if (!screen)
	{
		// Log.Debug("Fit()", "Fit() no screen to fit");
		return;
	}

	// ignore aspect ratio
	if (fittype == "stretch")
	{
		let designedWidth = Settings.ScreenWidth;
		let screenWidth = windowRect.width;
		let xratio = screenWidth / designedWidth;

		let designedHeight = Settings.ScreenHeight * Settings.Screens;
		if (Settings.VirtualBotoneraType != VirtualBotoneraType.Disable)
		{
			designedHeight += Settings.bottoneraFakeSize.Height;
		}

		let screenHeight = windowRect.height;
		let yratio = screenHeight / designedHeight;

		screen.style.transform = "scale(" + xratio + ", " + yratio + ")";
	}

	// use aspect ratio
	else if (fittype == "fit")
	{
		let width = Settings.ScreenWidth;
		let height = Settings.ScreenHeight * Settings.Screens;
		if (Settings.VirtualBotoneraType != VirtualBotoneraType.Disable)
		{
			height += Settings.bottoneraFakeSize.Height;
		}

		let fittedGameRatio: number = Geometry.FitRectRatio(new Rect(0, 0, width, height), windowRect);
		// Log.Debug("Fit()", `Fitting ${width}x${height} into ${windowRect.width}x${windowRect.height}, scale: ${fittedGameRatio}`);
		
		screen.style.transform = "scale(" + fittedGameRatio + ")";
	}
}
